<template>
    <Pane
        v-if="$route.params.id"
        class="request_pane prevent-close"
        :size="requestPaneSize"
        max-size="80"
        min-size="35"
    >
        <div class="request-view">
            <VScroll ref="scrollbar" suppress-scroll-x class="request-view__scrollbar">
                <div class="request-page-loader" v-if="isLoading">
                    <VSpinner></VSpinner>
                </div>

                <div class="request-page-error" v-if="errorMessage">
                    <span>{{ errorMessage }}</span>
                </div>

                <template v-else-if="request">
                    <div class="request-page" :class="{ 'request-page_loading': isLoading }">
                        <div class="request-page__row request-page__row_header">
                            <RequestViewHeader
                                :request="request"
                                :is-claiming="isClaiming"
                                @claim="onClaim"
                                @copy="createFromRequest"
                                @pause="onPause"
                                @resume="onResume"
                                @reject="onReject"
                                @revoke="onRevoke"
                                @accept="onAccept"
                                @complete="onComplete"
                                @revision="onRevision"
                                @actualize="onActualize"
                                @actualizeAdmin="onActualizeAdmin"
                            ></RequestViewHeader>
                        </div>
                        <div v-if="claimMessage" class="request-page__claim-message" role="alert">
                            {{ claimMessage }}
                        </div>

                        <div class="request-page__row request-page__row_card" v-if="showRequestInfo">
                            <RequestViewCard
                                :request="request"
                                @changeService="changeService"
                                @changeCategory="changeService"
                                @changeExecutor="changeExecutor"
                                @changePriority="changePriority"
                            ></RequestViewCard>
                        </div>

                        <div class="request-page__row request-page__row_lifecycle">
                            <RequestLifecycle :request="request"></RequestLifecycle>
                        </div>

                        <div class="request-page__row request-page__row_relation" v-if="request.hasParentRequest">
                            <RequestViewParentLink
                                :request="request"
                                @changed="UPDATE_REQUEST_IN_CONTEXT"
                            ></RequestViewParentLink>
                        </div>

                        <div class="request-page__row request-page__row_relation" v-if="request.hasBasedOnRequest">
                            <RequestViewBasedOn :request="request"></RequestViewBasedOn>
                        </div>

                        <div class="request-page__row request-page__tabs">
                            <Tabs>
                                <Tab id="chat" name="Комментарии">
                                    <RequestViewTabChat
                                        :request="request"
                                        @addComment="addComment"
                                        @sendToBitrix24="sendToBitrix24"
                                        @readComment="readComment"
                                        @editComment="editComment"
                                        @changeStatus="setRequestStatus"
                                    ></RequestViewTabChat>
                                </Tab>
                                <Tab id="history" name="История">
                                    <RequestViewTabHistory
                                        :request="request"
                                        :history="request.history"
                                    ></RequestViewTabHistory>
                                </Tab>

                                <Tab id="observers" name="Наблюдатели">
                                    <RequestViewTabObservers
                                        :request="request"
                                        :observers="request.observers"
                                        @addObserver="addObserver"
                                        @deleteObserver="deleteObserver"
                                    ></RequestViewTabObservers>
                                </Tab>

                                <!-- Оборудование обращения (7-G1). Только при праве просмотра инвентаря:
                                     без него бэкенд всё равно отдаёт пустой список, и вкладка
                                     с вечным «не привязано» только вводила бы в заблуждение. -->
                                <Tab id="inventory" name="Оборудование" v-if="canViewInventory">
                                    <RequestViewTabInventory :request="request"></RequestViewTabInventory>
                                </Tab>

                                <!-- Массовая проблема: только исполнителю и администратору и только
                                     в обращении, которое может быть главным (связанное само главным
                                     не становится, пока его не отвяжут). -->
                                <Tab id="related" name="Связанные обращения" v-if="showRelatedTab">
                                    <RequestViewTabRelated :request="request"></RequestViewTabRelated>
                                </Tab>
                            </Tabs>

                            <VToggleButtonLink class="request-page__roll-button" v-model="showRequestInfo"
                                >{{ showRequestInfo ? 'Свернуть' : 'Развернуть' }}
                            </VToggleButtonLink>
                        </div>
                    </div>
                </template>
            </VScroll>
        </div>
    </Pane>
</template>

<script>
// Components
import { Tabs, Tab } from 'vue-tabs-component';
import VToggleButtonLink from '@/views/components/VButton/ToggleButtonLink.vue';

import RequestViewCard from './components/RequestViewCard.vue';
import RequestLifecycle from './components/RequestLifecycle.vue';
import RequestViewHeader from './components/RequestViewHeader.vue';
import RequestViewTabChat from './components/RequestViewTabChat.vue';
import RequestViewTabHistory from './components/RequestViewTabHistory.vue';
import RequestViewTabObservers from './components/RequestViewTabObservers.vue';
import RequestViewTabInventory from './components/RequestViewTabInventory.vue';
import RequestViewTabRelated from './components/RequestViewTabRelated.vue';
import RequestViewParentLink from './components/RequestViewParentLink.vue';
import RequestViewBasedOn from './components/RequestViewBasedOn.vue';
import VSpinner from '@/views/components/VProgress/VSpinner.vue';
import { Pane } from 'splitpanes';
import VScroll from '@/views/components/VScroll/VScroll.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import ModalNames from '@/js/enums/ModalNames';
import RequestStatusType from '@/js/enums/RequestStatusType';
import { changeRequestStatus, claimRequest, getLastReadComment } from '@/js/api';
import ErrorMessages from '@/js/enums/ErrorMessages';
import last from 'lodash/last';
import {
    addRequestComment,
    addRequestObserver,
    changeRequestExecutor,
    changeRequestPriority,
    changeRequestService,
    createTaskInBitrix24FromRequest,
    deleteRequestObserver,
    editRequestComment,
    findRequestById,
} from '../../../js/api';
import { Watch, Prop } from 'vue-property-decorator';
import RequestCommentType from '../../../js/enums/RequestCommentType';
import { canChangePriority } from '@/js/requestPriority';
import { canShowRelatedTab } from '@/js/requestRelations';

const Workspace = namespace('workspace');

@Component({
    components: {
        VSpinner,
        VToggleButtonLink,
        Pane,
        VScroll,

        RequestViewTabChat,
        RequestViewTabHistory,
        RequestViewTabObservers,
        RequestViewTabInventory,
        RequestViewTabRelated,
        RequestViewParentLink,
        RequestViewBasedOn,

        Tab,
        Tabs,

        RequestViewCard,
        RequestLifecycle,
        RequestViewHeader,
    },
})
class RequestView extends Vue {
    @Workspace.State((state) => state.activeRequest) request;
    @Workspace.State activeRequests;
    @Workspace.State inventoryPermissions;

    @Workspace.Mutation SET_ACTIVE_REQUEST;
    @Workspace.Mutation UPDATE_REQUEST_IN_CONTEXT;
    @Workspace.Mutation ADD_REQUEST_OBSERVER;
    @Workspace.Mutation DELETE_REQUEST_OBSERVER;
    @Prop({ type: Number, default: 40 })
    requestPaneSize;

    isLoading = false;
    isClaiming = false;
    claimMessage = '';
    errorMessage = '';
    showRequestInfo = true;
    lastCommentToSend = null;
    lastCommentTimer = null;

    // Право раздела приходит с бэкенда готовым (source-data), по ролям здесь ничего не считается.
    get canViewInventory() {
        return Boolean(this.inventoryPermissions && this.inventoryPermissions.view);
    }

    get showRelatedTab() {
        return canShowRelatedTab(this.request, { isExecutor: this.$_isExecutor, isAdmin: this.$_isAdmin });
    }

    async createFromRequest() {
        try {
            const created = await this.$showModal(ModalNames.REQUEST_CREATE, { basedOnRequestId: this.request.id });
            if (created && created.id) {
                this.$router.push({ name: 'request', params: { method: 'outbox', id: created.id } });
            }
        } catch (error) {
            // Closing the modal rejects its promise.
        }
    }

    async addComment(event) {
        if (!this.request || this.isLoading) {
            return;
        }

        const shouldOfferAcceptance =
            this.request.status === RequestStatusType.FINISH && this.request.authorId === this.$_account.id;
        const files = event.files;
        const comment = event.comment;

        // If a comment is not exists and one of files aren't uploaded.
        if (!comment && files?.every((x) => x.id === -1)) {
            return;
        }

        this.isLoading = true;

        try {
            const response = await addRequestComment(this.request.id, {
                files,
                comment,
                ...(event.replyToId ? { reply_to_id: event.replyToId } : {}),
            });
            this.UPDATE_REQUEST_IN_CONTEXT(response.data);

            if (shouldOfferAcceptance && response.data.status === RequestStatusType.FINISH) {
                await this.offerAcceptanceAfterComment(response.data);
            }
        } catch (error) {
            this.errorMessage = this.$errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    async offerAcceptanceAfterComment(request) {
        let nextStatus = null;

        try {
            await this.$showModal(ModalNames.CONFIRM, {
                text: 'Обращение выполнено. Выберите действие: принять или вернуть в работу!',
                title: 'Принять работу',
                cancelTitle: 'Вернуть в работу',
                confirmTitle: 'Принять работу',
                cancelAction: () => (nextStatus = RequestStatusType.WORKUP),
                confirmAction: () => (nextStatus = RequestStatusType.CLOSED),
            });
        } catch (error) {
            return;
        }

        if (nextStatus === RequestStatusType.CLOSED) {
            await this.onAccept();
        } else if (nextStatus === RequestStatusType.WORKUP) {
            await this.setRequestStatus(request, RequestStatusType.WORKUP);
        }
    }

    async editComment(event) {
        if (!this.request || this.isLoading) {
            return;
        }

        const commentId = event.commentId;
        const newText = event.newText;

        this.isLoading = true;

        try {
            const response = await editRequestComment(this.request.id, commentId, newText);
            this.UPDATE_REQUEST_IN_CONTEXT(response.data);
        } catch (error) {
            this.errorMessage = this.$errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    async addObserver(observer) {
        this.isLoading = true;

        try {
            const response = await addRequestObserver(this.request.id, {
                observerId: observer.id,
            });

            this.ADD_REQUEST_OBSERVER({
                request: this.request,
                observer: response.data,
            });
        } catch (error) {
            this.errorMessage = this.$errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    async deleteObserver(observer) {
        this.isLoading = true;

        try {
            await deleteRequestObserver(this.request.id, observer.id);

            this.DELETE_REQUEST_OBSERVER({
                request: this.request,
                observerId: observer.id,
            });
        } catch (error) {
            this.errorMessage = this.$errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    async changeService() {
        // If current doesn't have executor role, we do not allow to change the service.
        if (!this.$_isExecutor) {
            return;
        }

        let changeServiceParams = null;

        try {
            changeServiceParams = await this.$showModal(ModalNames.REQUEST_CHANGE_SERVICE, {
                request: this.request,
            });
        } catch (error) {
            // the user canceled the changes of the service.
            return;
        }

        try {
            this.isLoading = true;

            const response = await changeRequestService(this.request.id, changeServiceParams);

            this.UPDATE_REQUEST_IN_CONTEXT(response.data);
        } catch (error) {
            this.errorMessage = this.$errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    async changeExecutor() {
        // If current doesn't have executor role, we do not allow to change the executor.
        if (!this.$_isExecutor) {
            return;
        }

        let changeExecutorParams = null;

        try {
            changeExecutorParams = await this.$showModal(ModalNames.REQUEST_CHANGE_EXECUTOR, {
                request: this.request,
            });
        } catch (error) {
            // The user canceled the changes of the executor.
            return;
        }

        try {
            this.isLoading = true;

            const response = await changeRequestExecutor(this.request.id, changeExecutorParams);

            this.UPDATE_REQUEST_IN_CONTEXT(response.data);
        } catch (error) {
            this.errorMessage = this.$errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    async onClaim() {
        if (!this.request || this.isClaiming) return;

        const requestId = this.request.id;
        this.isClaiming = true;
        this.claimMessage = '';

        try {
            const { data } = await claimRequest(requestId);
            this.UPDATE_REQUEST_IN_CONTEXT(data);
            this.$bus.emit('request:claimed');
        } catch (error) {
            const status = error.response?.status;
            if (status === 409) {
                this.claimMessage = 'Обращение уже взято другим сотрудником.';
                const current = await this.getRequestById(requestId, false);
                if (current) this.UPDATE_REQUEST_IN_CONTEXT(current);
                this.$bus.emit('request:claimed');
            } else if (status === 403) {
                this.claimMessage = 'У вас нет прав взять это обращение в работу.';
            } else {
                this.claimMessage = 'Не удалось взять обращение в работу. Попробуйте ещё раз.';
            }
        } finally {
            this.isClaiming = false;
        }
    }

    async changePriority() {
        // Исполнитель или администратор; бэкенд проверит то же самое (role.staff).
        if (!canChangePriority({ isExecutor: this.$_isExecutor, isAdmin: this.$_isAdmin }, this.request)) {
            return;
        }

        let params = null;

        try {
            params = await this.$showModal(ModalNames.REQUEST_CHANGE_PRIORITY, {
                request: this.request,
            });
        } catch (error) {
            // The user canceled the changes of the priority.
            return;
        }

        if (!params || params.priority === this.request.priority) {
            return;
        }

        try {
            this.isLoading = true;

            const response = await changeRequestPriority(this.request.id, params.priority);

            this.UPDATE_REQUEST_IN_CONTEXT(response.data);
        } catch (error) {
            this.errorMessage = this.$errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    async sendToBitrix24() {
        let title = null;
        let groupId = null;

        try {
            var result = await this.$showModal(ModalNames.REQUEST_SEND_TO_BITRIX24);

            title = result.title;
            groupId = result.groupId;
        } catch (error) {
            // The user canceled the changes of the executor.
            return;
        }

        try {
            this.isLoading = true;

            const response = await createTaskInBitrix24FromRequest(groupId, {
                title,
                requestId: this.request.id,
            });

            this.UPDATE_REQUEST_IN_CONTEXT(response.data);
        } catch (error) {
            this.errorMessage = this.$errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    async onPause() {
        try {
            const params = await this.$showModal(ModalNames.REQUEST_PAUSE);

            this.setRequestStatus(this.request, RequestStatusType.PAUSED, params);
        } catch (error) {
            return;
        }
    }

    onResume() {
        this.setRequestStatus(this.request, RequestStatusType.WORKUP);
    }

    async onReject() {
        try {
            const params = await this.$showModal(ModalNames.REQUEST_SOLUTION, {
                title: 'Укажите причину отказа',
                fieldName: 'причина отказа',
            });
            this.setRequestStatus(this.request, RequestStatusType.DENIED, params);
        } catch (error) {
            return;
        }
    }

    onRevoke() {
        this.setRequestStatus(this.request, RequestStatusType.CLOSED);
    }

    async onAccept() {
        try {
            const result = await this.$showModal(ModalNames.REQUEST_ACCEPT_WORK);
            await this.setRequestStatus(this.request, RequestStatusType.CLOSED, {
                rating: result.rating,
                comment: result.comment,
            });
        } catch (error) {
            return;
        }
    }

    async onComplete() {
        const lastComment = last(this.request.comments);
        try {
            const params = await this.$showModal(
                ModalNames.REQUEST_SOLUTION,
                this.$_isExecutor
                    ? {
                          lastComment:
                              lastComment.authorId === this.$_account.id &&
                              lastComment.type === RequestCommentType.COMMENT
                                  ? lastComment
                                  : undefined,
                      }
                    : undefined,
            );
            this.setRequestStatus(this.request, RequestStatusType.FINISH, params);
        } catch (error) {
            return;
        }
    }

    async onRevision() {
        const lastComment = last(this.request.comments);
        try {
            const params = await this.$showModal(ModalNames.REQUEST_SOLUTION, {
                title: 'Напишите комментарий',
                fieldName: 'Комментарий',
                ...(this.$_isExecutor
                    ? {
                          lastComment:
                              lastComment.authorId === this.$_account.id &&
                              lastComment.type === RequestCommentType.COMMENT
                                  ? lastComment
                                  : undefined,
                      }
                    : undefined),
            });
            this.setRequestStatus(this.request, RequestStatusType.REWORK, params);
        } catch (error) {
            return;
        }
    }

    onActualize() {
        this.setRequestStatus(this.request, RequestStatusType.WORKUP);
    }

    async onActualizeAdmin() {
        try {
            const params = await this.$showModal(ModalNames.REQUEST_SOLUTION, {
                title: 'Напишите причину возврата обращения в работу',
                fieldName: 'Причина возврата',
            });
            this.setRequestStatus(this.request, RequestStatusType.WORKUP, params);
        } catch (error) {
            return;
        }
    }

    async setRequestStatus(request, status, params = {}) {
        this.isLoading = true;

        try {
            var response = await changeRequestStatus(request.id, status, params);
            this.UPDATE_REQUEST_IN_CONTEXT(response.data);
            this.$bus.emit('request:status-changed');
        } catch (error) {
            console.error(error);

            this.errorMessage = 'Что-то пошло не так, попробуйте перезагрузить страницу';
        } finally {
            this.isLoading = false;
        }
    }

    cleanContext() {
        this.isLoading = false;
    }

    readComment(comment) {
        const lastSeenCommentByCurrentUser = this.request.lastSeenComments.find(
            (comment) => comment.userId === this.$_account.id,
        );

        if ((lastSeenCommentByCurrentUser?.commentId || 0) >= comment.id) {
            return;
        }
        if (!this.lastCommentToSend || this.lastCommentToSend.id <= comment.id) {
            this.lastCommentToSend = comment;
            if (!this.lastCommentTimer) {
                this.lastCommentTimer = setTimeout(this.sendReadComment, 1000);
            }
        }
    }

    async sendReadComment() {
        const response = await getLastReadComment(this.request, this.lastCommentToSend);
        this.UPDATE_REQUEST_IN_CONTEXT(response.data);
        this.lastCommentToSend = null;
        this.lastCommentTimer = null;
    }

    checkNewComment() {
        if (!this.request) {
            return;
        }

        const lastSeenCommentByCurrentUser = this.request.lastSeenComments.find(
            (comment) => comment.userId === this.$_account.id,
        );
        const comment = last(this.request.comments) || [];
        if (comment && (lastSeenCommentByCurrentUser?.id || 0) <= comment.id) {
            getLastReadComment(this.request, comment);
        }
    }

    async getRequestById(id, showError = true) {
        if (!id) {
            return;
        }

        this.isLoading = true;

        try {
            const response = await findRequestById(id, [
                'comments',
                'history',
                'observers',
                'service',
                'category',
                'author',
                'executor',
                'creator',
                'lastSeenComments',
                'securityGroup',
                'securityGroup.accounts',
                'history.author',
                'comments.author',
                'comments.files',
            ]);
            return response.data;
        } catch (error) {
            console.error(error);
            if (showError) this.errorMessage = ErrorMessages.SOMETHING_WENT_WRONG;
        } finally {
            this.isLoading = false;
        }
    }

    async fetchRequest() {
        this.isLoading = true;
        let request = this.activeRequests.find((request) => request.id === this.requestId);

        if (!request) {
            request = await this.getRequestById(this.requestId);
        }

        if (request) {
            this.SET_ACTIVE_REQUEST(request);
        } else {
            this.SET_ACTIVE_REQUEST(null);
        }
        setTimeout(() => {
            this.isLoading = false;
        }, 300);
    }

    created() {
        this.fetchRequest();
    }

    @Watch('request')
    requestWatcher() {
        this.checkNewComment();
    }

    @Watch('$route.params.id')
    onQueryChange() {
        this.claimMessage = '';
        this.fetchRequest();
    }

    get requestId() {
        return parseInt(this.$route.params.id, 10) ?? null;
    }
}

export default RequestView;
</script>
<style lang="scss">
.request_pane {
    box-shadow: 0 0 0.6875rem 0 rgba(0, 0, 0, 0.07);
}
.request-view {
    height: 100%;
    width: 100%;
    background-color: white;
    position: relative;
    top: 0;
    right: 0;
}
.request-page {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    padding: 20px 0 0 0;

    &__claim-message {
        margin: 0 24px 12px;
        color: #b03a2e;
    }

    &_loading {
        opacity: 0.6;
        cursor: wait;
        user-select: none;
        pointer-events: none;
    }

    &__row {
        display: flex;

        &_card,
        &_header {
            margin: 0 24px;
        }

        &_card {
            flex-shrink: 1;
            margin-top: 12px;
            margin-bottom: 12px;
        }

        &_header {
            flex-shrink: 1;
            margin-bottom: 12px;
        }

        &_relation {
            flex-shrink: 0;
            margin: 0 24px 12px;

            & > * {
                width: 100%;
            }
        }

        &_lifecycle {
            flex-shrink: 0;
            margin: 0 24px 12px;

            & > * {
                width: 100%;
                min-width: 0;
            }
        }
    }

    &__tabs {
        position: relative;
        display: flex;
        flex-grow: 1;
        height: 100%;
        min-height: 0;
    }

    &__roll-button {
        position: absolute;
        top: 0;
        right: 24px;
        padding: 15px 12px;
    }

    &__unread_comment {
        align-items: center;
        margin-left: 3px;
        padding: 3px 4px 2px 4px;
        gap: 10px;
        width: 20px;
        height: 17px;
        background: #30c07c;
        color: whitesmoke;
        border-radius: 3px;
        font-family: var(--font-family);
        font-style: normal;
        font-weight: 500;
        line-height: 12px;
    }
}

// В split-pane вкладки переносятся по строкам, а «Свернуть» занимает отдельную колонку.
@media (min-width: $mobile-breakpoint + 1px) {
    .request-page {
        &__tabs {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            grid-template-rows: auto minmax(0, 1fr);

            & > .tabs-component {
                display: contents;
            }

            .tabs-component-tabs {
                grid-column: 1;
                grid-row: 1;
                min-width: 0;
                flex-wrap: wrap;
                align-content: start;
            }

            .tabs-component-tab {
                display: flex;
                flex: 0 1 auto;
                min-width: 0;
                min-height: 48px;
                height: auto;
            }

            .tabs-component-tab-a {
                display: flex;
                align-items: center;
                min-height: 48px;
                padding: 8px 12px;
                overflow-wrap: anywhere;
            }

            .tabs-component-panels {
                grid-column: 1 / -1;
                grid-row: 2;
                min-width: 0;
                min-height: 0;
            }
        }

        &__roll-button {
            position: static;
            grid-column: 2;
            grid-row: 1;
            align-self: stretch;
            display: flex;
            align-items: flex-start;
            margin-right: 24px;
            padding: 16px 12px;
            white-space: nowrap;
            border-bottom: 1px solid var(--tabs-component-tabs-border);
        }
    }
}

.request-page-loader {
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    align-items: center;
    justify-content: center;
    z-index: 100000;
}

.request-page-error {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
}

.last-comments {
    flex-direction: column;
    justify-content: center;
}
@media (max-width: $mobile-breakpoint) {
    .request-page {
        height: auto;
        &__roll-button {
            display: none;
        }
    }
}
</style>
